import { TFunction } from 'i18next';

import { ShippingNoticeLineItem } from 'support/types';
import { WebEDIRowData } from 'support/types/webediTable';

import { LineItemEntry } from '../types';

type LineItemDisplay = {
  name: string;
  senderArticleNumber?: string;
  receiverArticleNumber?: string;
  gtin?: string;
  unitOfMeasure?: string;
  /**
   * The present identifiers in their canonical display order (GTIN, then receiver
   * article, then sender article), already labelled and with absent values dropped.
   * Render sites should spread this instead of re-listing fields so the order stays
   * consistent everywhere.
   */
  identifiers: Array<string>;
};

/**
 * Single place that reads the display details off a flat line-item row's `item`,
 * so the cast lives here instead of being repeated at every render site. Identifier
 * values are returned pre-labelled (e.g. 'Supplier Article: DEMO-002') so the
 * shared LineItemIdentifiers stays a plain join and every render site shows the
 * same label. The unit of measure is returned as the raw value (e.g. 'EA', 'KGM')
 * — WebEDI shows the code verbatim rather than a translated abbreviation.
 *
 * i18n keys
 * t('webedi:packing_list.identifiers.gtin', { value })
 * t('webedi:packing_list.identifiers.receiver_article', { value })
 * t('webedi:packing_list.identifiers.sender_article', { value })
 */
export const getLineItemDisplay = (row: WebEDIRowData, t: TFunction): LineItemDisplay => {
  const lineItem = row as ShippingNoticeLineItem | undefined;
  const item = lineItem?.item;
  const senderInternal = item?.identifiers?.sender_internal;
  const receiverInternal = item?.identifiers?.receiver_internal;
  const gtin = item?.identifiers?.gtin;

  const senderArticleNumber = senderInternal
    ? t('webedi:packing_list.identifiers.sender_article', { value: senderInternal })
    : undefined;
  const receiverArticleNumber = receiverInternal
    ? t('webedi:packing_list.identifiers.receiver_article', { value: receiverInternal })
    : undefined;
  const gtinLabel = gtin ? t('webedi:packing_list.identifiers.gtin', { value: gtin }) : undefined;

  return {
    name: item?.name ?? '',
    senderArticleNumber,
    receiverArticleNumber,
    gtin: gtinLabel,
    unitOfMeasure: item?.unit_of_measure,
    identifiers: [gtinLabel, receiverArticleNumber, senderArticleNumber].filter((part): part is string =>
      Boolean(part),
    ),
  };
};

export const getDialogLineItem = (entry: LineItemEntry | undefined, t: TFunction) => {
  const { name, identifiers, unitOfMeasure } = entry
    ? getLineItemDisplay(entry.row, t)
    : { name: '', identifiers: [], unitOfMeasure: undefined };
  return { productName: name, identifiers, unitOfMeasure };
};
